import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BalanceSparkline } from '../../../../web/src/components/display/balance-sparkline.jsx';
import { Amount } from '../../../../web/src/components/display/amount.jsx';
import { DonutChart } from '../../../../web/src/components/display/donut-chart.jsx';
import { computeDonutSlices } from '../../../../web/src/components/display/donut-chart.js';
import { MockAppFrame } from '../mock-app-frame.jsx';
import { useSiteLang } from '../../layout/use-site-lang.js';
import { getVisualiseMockData } from '../mock-data/visualise-mock-data.js';
import { wealthByTypeData, getCurrencyInfo } from '../report-utils.js';
import { getBaseCurrency } from '../mock-data/mock-data.js';

/**
 * Visualise use-case mockup: an investment account summary card with the real
 * dual-band BalanceSparkline, showing irregular contributions (step line)
 * and volatile market value (linear line) with profit/loss bands — plus a
 * net-worth-by-type donut chart with a legend and explanation.
 * @returns {JSX.Element}
 */
export function VisualiseScreen() {
	const { t } = useTranslation();
	const lang = useSiteLang();
	const mock = getVisualiseMockData(lang);
	const gain = mock.balance - mock.principal;
	const pct = ((gain / mock.principal) * 100).toFixed(1);

	const base = getBaseCurrency(lang);
	const byType = useMemo(() => wealthByTypeData(lang), [lang]);
	const values = Object.fromEntries(byType.map((row) => [row.type, row.net]));
	const total = byType.reduce((sum, row) => sum + row.net, 0);
	const { entries } = computeDonutSlices(values);
	const baseInfo = getCurrencyInfo(base, lang);

	/**
	 * @param {number} amount
	 * @returns {import('react').ReactNode}
	 */
	const amt = (amount) => (
		baseInfo ? <Amount value={amount} decimalPlaces={baseInfo.decimalPlaces} symbol={baseInfo.symbol} /> : String(amount)
	);

	return (
		<>
			<MockAppFrame url={mock.url}>
				<div className="card account-summary">
					<div className="card-header">
						<h2>{t('usecase.visualise.accountName')}</h2>
					</div>
					<div className="form-body">
						<BalanceSparkline
							account={mock.account}
							history={mock.history}
							valuations={mock.valuations}
						/>
						<div className="account-summary-type">
							{t('option.Investment Account')}
						</div>
						<div className="account-summary-balance">
							<Amount value={mock.balance} decimalPlaces={mock.decimalPlaces} symbol={mock.symbol} />
						</div>
						<div className={`account-summary-gain ${gain >= 0 ? 'positive' : 'negative'}`}>
							{gain >= 0 ? '+' : ''}
							<Amount value={Math.abs(gain)} decimalPlaces={mock.decimalPlaces} symbol={mock.symbol} />
							{' ('}{gain >= 0 ? '+' : ''}{pct}%{')'}
						</div>
					</div>
				</div>
			</MockAppFrame>
			<div className="card">
				<div className="card-header">
					<h2>{t('usecase.visualise.allocation.title')}</h2>
				</div>
				<div className="card-body allocation-body">
					<div className="wealth-grid">
						<DonutChart
							values={values}
							amount={amt(total)}
							label={t('usecase.visualise.allocation.centerLabel')}
							ariaLabel={t('usecase.visualise.allocation.title')}
						/>
						<div>
							<ul className="allocation-legend">
								{entries.map((entry) => (
									<li key={entry.key}>
										<span className={`swatch swatch-${entry.colorIndex}`} />
										<span className="allocation-name">{t('option.' + entry.key)}</span>
										<span className="allocation-value">{amt(values[entry.key])}</span>
										<span className="allocation-pct">{((values[entry.key] / total) * 100).toFixed(1)}%</span>
									</li>
								))}
							</ul>
						</div>
					</div>
				</div>
			</div>
			<div className="use-case-callout card">
				<h3>{t('usecase.visualise.atAGlance.title')}</h3>
				<p>{t('usecase.visualise.atAGlance.body')}</p>
			</div>
		</>
	);
}
